// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { handleFooterQuickLinkClick } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface FooterLink {
  label: string;
  link: string;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

/**
 * Dark themed footer component used to display brand logo, quick navigation links, social links, and copyright/legal text for website templates.
 * @component
 * @selector app-dark-page-footer
 * @example
 *   <app-dark-page-footer [logo]="{ text: 'Way', image: '/assets/logo.png' }" [backgroundColor]="'#000'"></app-dark-page-footer>
 * @input {{ text?: string; image?: string }} logo - Optional logo object with an optional display text and optional image URL used to brand the footer. Defaults to undefined.
 * @input {FooterLink[]} quickLinks - Array of quick navigation links rendered in the footer. Default includes links such as Home, Express Washes, Coupons, Memberships, Express Details, and Fleets.
 * @input {string} quickLinksTitle - Title string displayed above the quick links section. Default: 'Quick links'.
 * @input {SocialLink[]} socialLinks - Array of social link objects (platform, url, icon) rendered as social icons/links. Default includes Instagram, Facebook, YouTube, LinkedIn, and X.
 * @input {string} socialLinksTitle - Title string displayed above the social links section. Default: 'Connect with us'.
 * @input {string} copyright - Copyright/legal text shown in the footer. Default: `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`.
 * @input {string} backgroundColor - Optional CSS color value used as the footer background; when omitted the component uses its default dark styling.
 * @output {void} none - This component does not emit any output events.
 *
 * Key behavior:
 * - Purely presentational: arranges provided inputs into a dark-themed footer layout (logo area, quick links, social links, and copyright).
 * - Accepts arrays of links and titles to customize content without internal state management.
 *
 * Methods:
 * - No public methods are provided; the component relies on inputs for configuration and rendering.
 *
 * Lifecycle hooks:
 * - No component-specific Angular lifecycle hooks (e.g., ngOnInit) are required or implemented for basic rendering; it is implemented as a standalone, stateless component.
 */
@Component({
  selector: 'app-dark-page-footer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer.component.scss'],
})
export class DarkPageFooterComponent {
  @Input() logo?: {
    text?: string;
    image?: string;
  };

  @Input() quickLinks: FooterLink[] = [
    { label: 'Home', link: '#home' },
    { label: 'Express Washes', link: '#express-wash-packages' },
    { label: 'Coupons', link: '#car-wash-coupons--way' },
    { label: 'Memberships', link: '#memberships' },
    { label: 'Express Details', link: '#express-auto-details' },
    { label: 'Fleets', link: '#fleet-management' },
  ];

  @Input() quickLinksTitle = 'Quick links';

  @Input() socialLinks: SocialLink[] = [
    { platform: 'Instagram', url: '#', icon: 'instagram' },
    { platform: 'Facebook', url: '#', icon: 'facebook' },
    { platform: 'YouTube', url: '#', icon: 'youtube' },
    { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
    { platform: 'X', url: '#', icon: 'x' },
  ];

  @Input() socialLinksTitle = 'Connect with us';

  @Input() copyright = `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;

  @Input() backgroundColor?: string;
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() quickLinkClick = new EventEmitter<string>();

  onQuickLinkClick(event: Event, link: FooterLink): void {
    handleFooterQuickLinkClick(event, link, pageType => this.quickLinkClick.emit(pageType));
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
